import React, { JSX } from "react";
import { CartesianGrid, Dot, Label, ResponsiveContainer, Scatter, ScatterChart, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { HospitalAndPcpCountData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { tickFormatter } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { useHoverBoundary } from "@app/shared/hooks/useHoverBoundary";

interface HospitalAndPcpCountProps {
  canDrillIn: boolean;
  colorMap: Map<string, string>;
  customTooltip: (
    props: TooltipContentProps<ValueType, string | number>,
    titleKey: string,
  ) => JSX.Element;
  data: HospitalAndPcpCountData[];
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
    isHospitalAndPcp?: boolean,
  ) => void;
}

// this is an incomplete interface, but will suffice
interface HospitalAndPcpScatterProps {
  cx: number;
  cy: number;
  payload?: {
    acuteHospitalCount: number;
    contractId: string;
    pcpCount: number;
    versionId: number;
  };
}

const HospitalAndPcpCount = (props: HospitalAndPcpCountProps): JSX.Element => {
  const { isHovered: isChartHovered, ref: chartRef } = useHoverBoundary<HTMLDivElement>();

  const onDotClick = (data: HospitalAndPcpScatterProps): void => {
    if (!!data?.payload && props.canDrillIn) {
      props.onDashboardDetailsViewClick(
        BenchmarkingDashboardDetailsTypeEnum.NetworkDrillin,
        data.payload.pcpCount + data.payload.acuteHospitalCount,
        data.payload.versionId,
        null,
        null,
        true,
      );
    }
  }

  const renderDot = (cx: number, cy: number, payload: HospitalAndPcpCountData): JSX.Element => {
    // rendering individually in order to assign a radius size to the dot
    return (
      <Dot
        cursor={props.canDrillIn ? "pointer" : "default"}
        id={`benchmarking-dashboard__hospital-and-pcp-count--contract-${payload?.contractId}-hospital-${payload?.acuteHospitalCount}-pcp-${payload?.pcpCount}`}
        cx={cx}
        cy={cy}
        r={20}
        fill={props.colorMap.get(payload?.contractId)}
      />
    );
  }

  return (
    <React.Fragment>
      {!!props.data?.length ? (
        <div
          ref={chartRef}
          style={{ height: "300px", width: "100%" }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{
                bottom: 10,
                left: 0,
                right: 0,
                top: 20,
              }}
            >
              <CartesianGrid strokeDasharray="1 0" horizontal={false} />
              <XAxis dataKey="pcpCount" type="number" tickLine={false} tickFormatter={tickFormatter}>
                <Label value="PCP Count" offset={-10} position="insideBottom" />
              </XAxis>
              <YAxis dataKey="acuteHospitalCount" type="number" tickLine={false} tickFormatter={tickFormatter}>
                <Label value="Acute Hospital Count" position="insideLeft" angle={-90} offset={5} style={{ textAnchor: "middle" }} />
              </YAxis>
              <Tooltip content={(customTooltipProps) => props.customTooltip(customTooltipProps, "contractId")} active={isChartHovered ? undefined : false} />
              <Scatter data={props.data} shape={(props: HospitalAndPcpScatterProps) => renderDot(props.cx, props.cy, props.payload)} onClick={onDotClick} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="copy-text-default fst-italic mb-0">
          No results currently displayed. Input search parameters to get results.
        </p>
      )}
    </React.Fragment>
  )
}

export default HospitalAndPcpCount;
